import React from "react";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";

import { ToastContext } from "@weezevent/nacre";

import Checkpoint, {
	INACTIVE_STATUS,
	ACTIVE_STATUS,
	ARCHIVE_STATUS
} from "models/checkpoint";
import { ToastMessageWithCta } from "components/generic/toastMessage";
import { CONSTANTS } from "reducers/checkpoint";
import { PATCH } from "utils/fetcher";

const updateCheckpointsStatus = (checkpoints, checkpointsIds, status) => {
	return checkpoints.map(checkpoint => {
		if (checkpointsIds.indexOf(checkpoint.id()) >= 0) {
			return new Checkpoint(
				{
					...checkpoint.checkpoint,
					status
				},
				checkpoint.organizationId
			);
		} else {
			return checkpoint;
		}
	});
};

const removeCheckpoints = (checkpoints, checkpointsIds) =>
	checkpoints.filter(
		checkpoint => checkpointsIds.indexOf(checkpoint.id()) === -1
	);

const statusFilterMapping = {
	[CONSTANTS.OFF]: [INACTIVE_STATUS],
	[CONSTANTS.ON]: [ACTIVE_STATUS],
	[CONSTANTS.ARCHIVED]: [ARCHIVE_STATUS],
	[CONSTANTS.NOT_ARCHIVED]: [ACTIVE_STATUS, INACTIVE_STATUS]
};

function patchCheckpoints(organizationId, json) {
	return PATCH(
		`/bo/organizers/${organizationId}/checkpoints/`,
		JSON.stringify(json)
	);
}

export default function useChangeCheckpointsStatus(
	organizationId,
	filters,
	queryKey
) {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { success } = React.useContext(ToastContext);

	const cancelMutation = async originalCheckpoints => {
		try {
			await patchCheckpoints(
				organizationId,
				// just patch back each checkpoint with its original status
				// it works because we're not mutating the checkpoints in `onMutate`
				// instead we replace them with new instances
				originalCheckpoints.map(c => ({
					id: c.id(),
					status: c.status()
				}))
			);
		} finally {
			queryClient.invalidateQueries(queryKey);
		}
	};

	return async ({ status, checkpoints }) => {
		// optimistic update
		await queryClient.cancelQueries("checkpoints");
		const previousData = queryClient.getQueryData(queryKey);

		const checkpointsIds = checkpoints.map(c => c.id());
		const doUpdate =
			!filters.status ||
			statusFilterMapping[filters.status].indexOf(status) >= 0;

		queryClient.setQueryData(queryKey, old => ({
			...old,
			data: doUpdate
				? updateCheckpointsStatus(old.data, checkpointsIds, status)
				: removeCheckpoints(old.data, checkpointsIds)
		}));

		// request
		try {
			await patchCheckpoints(
				organizationId,
				checkpoints.map(c => ({ id: c.id(), status }))
			);

			let message;
			const isSingular = checkpoints.length === 1;
			if (status === INACTIVE_STATUS) {
				message = t(
					isSingular
						? "access.checkpoint.toasts.success.inactive"
						: "access.checkpoint.toasts.success.inactive_plural",
					{
						count: checkpoints.length
					}
				);
			} else if (status === ACTIVE_STATUS) {
				message = t(
					isSingular
						? "access.checkpoint.toasts.success.active"
						: "access.checkpoint.toasts.success.active_plural",
					{
						count: checkpoints.length
					}
				);
			} else if (status === ARCHIVE_STATUS) {
				message = t(
					isSingular
						? "access.checkpoint.toasts.success.archived"
						: "access.checkpoint.toasts.success.archived_plural",
					{
						count: checkpoints.length
					}
				);
			}

			success(
				<ToastMessageWithCta
					className="toast-success"
					title=""
					text={message}
					cta={t("access.common.cancel")}
					onClick={() => cancelMutation(checkpoints)}
				/>,
				{
					pauseOnHover: true
				}
			);
		} catch {
			queryClient.setQueryData(
				["checkpoints", organizationId, filters],
				previousData
			);
		} finally {
			queryClient.invalidateQueries(queryKey);
		}
	};
}
